import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  LundifyEmailPortalResponse,
  LundifySubscriptionResponse,
  LundifySubscriptionUser,
} from '../state/types/lundify-subscription-response.types';
import { BLService } from './bl.service';
import { environment } from 'src/environments/environment';
import { HttpAuthorizationHeader } from '../misc/httpauthorizationheader';

@Injectable({
  providedIn: 'root',
})
export class LundifySubscriptionsService extends BLService {
  private options = new HttpAuthorizationHeader('application/json');
  private RESOURCE_ENDPOINT = 'cloud-company';
  private url = environment.serverUrlV2 + this.RESOURCE_ENDPOINT;

  loadSubscriptionInformation(clientId: number | string): Observable<LundifySubscriptionResponse> {
    return this.http.get<LundifySubscriptionResponse>(
      `${this.url}/${clientId}/lundify/company-subscription`,
      this.options.getAuthorizationHeaderWithEmptyBody(),
    );
  }

  updatePayingPart(
    clientId: number | string,
    body: { reference: string; email: string; phoneNumber: string } | null,
  ): Observable<LundifySubscriptionResponse> {
    return this.http.put<LundifySubscriptionResponse>(
      `${this.url}/${clientId}/company-subscription/paying-part`,
      body,
      this.options.getAuthorizationHeader(),
    );
  }

  loadSubscriptionUsers(clientId: number | string): Observable<LundifySubscriptionUser[]> {
    return this.http.get<LundifySubscriptionUser[]>(
      `${this.url}/${clientId}/lundify/company-subscription/users`,
      this.options.getAuthorizationHeaderWithEmptyBody(),
    );
  }

  addSubscriptionUser(clientId: number | string, body: LundifySubscriptionUser): Observable<LundifySubscriptionUser> {
    return this.http.post<LundifySubscriptionUser>(
      `${this.url}/${clientId}/lundify/company-subscription/users`,
      body,
      this.options.getAuthorizationHeader(),
    );
  }

  getEmailPortalSettings(clientId: number | string): Observable<LundifyEmailPortalResponse> {
    return this.http.get<LundifyEmailPortalResponse>(
      `${this.url}/${clientId}/lundify/company-subscription/email-portal-settings`,
      this.options.getAuthorizationHeaderWithEmptyBody(),
    );
  }
}

// SCHEMA AND EXAMPLE FOR LUNDIFY SUBSCRIPTION RESPONSE
// {
//   publicKey: '21fb9d73-2c32-4d9b-976c-536e61a5ac8c',
//   isClientPaying: false,
//   hasActiveLicensedUser: true,
//   payingCustomerNbr: 123456,
//   firmCustomerNbr: 654321,
//   modules: [
//     {
//       id: 100,
//       displayName: 'Årskund',
//       blaProductId: 'appannual',
//       price: 0,
//       priceAdditionalUser: 0,
//       description: 'Årskund',
//       paymentType: 'user',
//       type: 'bundle',
//       icon: null,
//       recurring: 'monthly',
//       validityDays: null,
//       properties: [],
//       isAddon: null,
//       bundleContents: [
//         {
//           id: 131,
//           data: 'Bokföring',
//           langId: 'BUNDLE_CONTENTS_ACCOUNTING',
//         },
//       ],
//       modules: [
//         {
//           id: 102,
//           displayName: 'Ekonomiöversikt',
//           blaProductId: 'appannualfinancialoverview',
//           price: 0,
//           priceAdditionalUser: 0,
//           description:
//             'Ger användaren en tydlig överblick över företagets ekonomi. Ekonomiöversikten ingår i Björn Lundén Bokföring men kan beställas som tilläggstjänst till någon inom eller utom organisationen som har behov av att följa företagets ekonomiska status och utveckling.',
//           paymentType: 'user',
//           type: 'module',
//           icon: null,
//           recurring: 'monthly',
//           validityDays: null,
//           properties: [
//             {
//               id: 21,
//               label: 'Ekonomiöversikt',
//               name: 'accountingView',
//               defaultValue: true,
//               parentId: null,
//               type: 'CHECKBOX',
//               properties: [],
//               langId: 'PROPERTY_ACCOUNTING_VIEW',
//             },
//           ],
//           isAddon: false,
//           bundleContents: [],
//           modules: [],
//           langId: 'MODULE_FINANCIAL_OVERVIEW_ANNUAL',
//           deprecated: false,
//         },
//         {
//           id: 101,
//           displayName: 'Bokföring',
//           blaProductId: 'appannualaccounting',
//           price: 0,
//           priceAdditionalUser: 0,
//           description:
//             'Ger användaren möjlighet att skapa verifikationer, leverantörsfakturor, bokföringskonton och räkenskapsår. Det finns också en funktion för att hantera momsen.',
//           paymentType: 'user',
//           type: 'module',
//           icon: null,
//           recurring: 'monthly',
//           validityDays: null,
//           properties: [
//             {
//               id: 39,
//               label: 'Bokföring',
//               name: 'accounting',
//               defaultValue: true,
//               parentId: null,
//               type: 'CHECKBOX',
//               properties: [],
//               langId: 'PROPERTY_ACCOUNTING',
//             },
//           ],
//           isAddon: false,
//           bundleContents: [],
//           modules: [],
//           langId: 'MODULE_ACCOUNTING_ANNUAL',
//           deprecated: false,
//         },
//       ],
//       langId: 'MODULE_BUNDLE_ANNUAL_CLIENT',
//       deprecated: false,
//     },
//   ],
//   subscriptionExpenseSnapshot: {
//     bundleExpenseSnapshot: {
//       id: 100,
//       name: 'Årskund',
//       regularSubscribers: 0,
//       allInclusiveSubscribers: 1,
//       totalSubscribers: 1,
//       price: 0.0,
//       totalCost: 0.0,
//       blaProductId: 'appannual',
//       amountOfTransactions: 0,
//       additionalSubscriptionCost: 0.0,
//     },
//     moduleExpenseSnapshot: [
//       {
//         id: 73,
//         name: 'Återaktivering av företag',
//         regularSubscribers: 0,
//         allInclusiveSubscribers: 0,
//         totalSubscribers: 0,
//         price: 690,
//         totalCost: 690,
//         blaProductId: 'app_arkiv_ater',
//         amountOfTransactions: 1,
//         additionalSubscriptionCost: 0.0,
//       },
//     ],
//     integrationExpenseSnapshot: [],
//     totalAmountBilled: 690.0,
//   },
//   partnerProgram: [
//     {
//       blaProductId: 'appannual',
//       users: [],
//     },
//   ],
// };
